// material-ui
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import MainCard from 'components/MainCard';

// assets
import { IdcardOutlined, RedoOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { config as apiConfig, selectedProfile } from '../../../../config/config';

const formatDate = (value) => {
  if(!value) return null;
  try {
    return format(parseISO(value), 'yyyy-MM-dd HH:mm');
  }
  catch(e) {
    return value;
  }
}

/**
 * Identification (KYC) card of the client (FULL_FLOW partners): current KYC status and the "generate identification link" button.
 * The link is the same 7 day client portal link as the bank consent one, only the notification differs (linkType = KYC);
 * the portal positions the client on the identification step and sends them to Sumsub.
 */
export default({model, clientId, personal, canSendEmail, canSendSms}) => {
  const dispatcher = useDispatch();
  const [t, i18n] = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [commMethod, setCommMethod] = useState('email');
  const [buttonEnabled, setButtonEnabled] = useState(true);
  const [sentAt, setSentAt] = useState(null);

  const kycStatus = personal?.kycStatus;
  const kycResult = personal?.kycResult;
  const scorePassed = personal?.kycScorePassed === true;
  const emailAddress = personal?.email;
  const phoneNumber = personal?.phone;
  const linkGeneratedAt = sentAt ? sentAt : formatDate(personal?.kycLinkGenerated);
  const reviewedAt = formatDate(personal?.kycReviewed);

  const passed = kycStatus === 'DONE' && kycResult === 'PASSED';
  const failed = kycStatus === 'DONE' && kycResult === 'NOT_PASSED';

  let statusKey = 'kyc_status_not_started';
  let statusColor = 'default';
  if(passed) {
    statusKey = 'kyc_status_passed';
    statusColor = 'success';
  }
  else if(failed) {
    statusKey = 'kyc_status_failed';
    statusColor = 'error';
  }
  else if(kycStatus === 'WAITING' && (personal?.kycLinkGenerated || personal?.kycRejectType)) {
    statusKey = personal?.kycRejectType === 'RETRY' ? 'kyc_status_retry' : 'kyc_status_waiting';
    statusColor = 'warning';
  }

  let description = t('kyc_link_desc');
  if(!scorePassed) {
    description = t('kyc_link_score_needed');
  }
  else if(passed) {
    description = t('kyc_passed_desc');
  }
  else if(failed) {
    description = t('kyc_failed_desc');
  }
  else if(linkGeneratedAt) {
    description = t('kyc_link_was_generated', {date: linkGeneratedAt});
  }

  const startProcess = () => {
    if(!clientId) {
      return;
    }
    setButtonEnabled(false);
    const sendEmail = commMethod == 'email';
    const sendSms = commMethod == 'sms';
    model.fetchClientAccessToken(dispatcher, clientId, sendEmail, sendSms, 'KYC').unwrap().then(res => {
      setButtonEnabled(true);
      setDialogOpen(false);
      if(res.success) {
        dispatcher(openSnackbar({open: true, message: t('kyc_link_generated'), variant: 'alert', alert: {color: 'success'}}));
        const portalLink = apiConfig[selectedProfile].MITIGATE_CLIENT_PORTAL_URL+"?client="+res.data.access_token;
        if(commMethod == 'clipboard') {
          navigator.clipboard.writeText(portalLink);
        }
        setSentAt(format(new Date(), 'yyyy-MM-dd HH:mm'));
      }
      else {
        dispatcher(openSnackbar({open: true, message: t('kyc_link_generation_error'), variant: 'alert', alert: {color: 'error'}}));
      }
    }, e => {
      setButtonEnabled(true);
      setDialogOpen(false);
      dispatcher(openSnackbar({open: true, message: t('kyc_link_generation_error'), variant: 'alert', alert: {color: 'error'}}));
    });
  }

  const canGenerate = scorePassed && !passed;

  return (
    <>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MainCard title={t('kyc_identification')} sx={{background: passed ? '#EDFFEF' : (failed ? '#FFF1F0' : '#F4F8FF')}}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <IdcardOutlined style={{ fontSize: '150%'}} />
                <Chip label={t(statusKey)} color={statusColor} size="small" variant={statusColor === 'default' ? 'outlined' : 'filled'} />
                {reviewedAt && (passed || failed) &&
                  <Typography variant="caption">{t('kyc_reviewed_at', {date: reviewedAt})}</Typography>
                }
              </Stack>
              <Typography sx={{marginTop: 2}}>{description}</Typography>
              {personal?.kycRejectType && !passed &&
                <Typography variant="caption" sx={{display: 'block', marginTop: 1}}>{t('kyc_reject_type', {type: personal.kycRejectType})}</Typography>
              }
              {canGenerate &&
                <Grid container spacing={0} direction="row" sx={{marginTop: 3}} justifyContent="flex-start">
                  <Button variant="shadow" disabled={!buttonEnabled} color="info" onClick={() => setDialogOpen(true)} endIcon={linkGeneratedAt ? <RedoOutlined /> : <RightOutlined />}>
                    {linkGeneratedAt ? t('kyc_regenerate_link') : t('kyc_generate_link')}
                  </Button>
                </Grid>
              }
            </MainCard>
          </Grid>
        </Grid>
      </Grid>
      <Dialog maxWidth="sm" fullWidth onClose={() => setDialogOpen(false)} open={dialogOpen} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        <DialogTitle>{t('kyc_generate_link')}</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2.5 }}>
          <Typography sx={{marginBottom: 2}}>{t('kyc_generate_link_desc')}</Typography>
          <FormControl fullWidth>
            <InputLabel id="kyc-communication-label" htmlFor="kyc-communication">{t('send_link_to')}</InputLabel>
            <Select
              labelId="kyc-communication-label"
              id="kyc-communication"
              onChange={(event) => setCommMethod(event.target.value)}
              value={commMethod}
              label={t('method_of_communication')}>
              {canSendEmail && emailAddress &&
                <MenuItem value={'email'}>{t('email_to_address', {'emailAddress': emailAddress})}</MenuItem>
              }
              {canSendSms && phoneNumber &&
                <MenuItem value={'sms'}>{t('sms_to_number', {'number': phoneNumber})}</MenuItem>
              }
              <MenuItem value={'clipboard'}>{t('copy_to_clipboard')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item></Grid>
            <Grid item>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button color="error" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={!buttonEnabled} onClick={startProcess}>
                  {t('save')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
}
